// 일정 관리(v3.26) — 날짜/표시 유틸. 설계문서 schedule-management-design.md v1.2.
import type { ScheduleItem } from "@/api/types";

/** 분야 배지 색 — 이름 해시 기반 고정 팔레트(채움). "공통"은 중립 진색. */
const FIELD_BADGES = [
  "bg-sky-100 text-sky-800",
  "bg-emerald-100 text-emerald-800",
  "bg-amber-100 text-amber-800",
  "bg-violet-100 text-violet-800",
  "bg-rose-100 text-rose-800",
  "bg-teal-100 text-teal-800",
  "bg-indigo-100 text-indigo-800",
  "bg-orange-100 text-orange-800",
  "bg-lime-100 text-lime-800",
  "bg-fuchsia-100 text-fuchsia-800",
  "bg-cyan-100 text-cyan-800",
  "bg-red-100 text-red-800",
  "bg-green-100 text-green-800",
  "bg-purple-100 text-purple-800",
];
/** 캘린더 점 색 — FIELD_BADGES 와 같은 순서(해시 동일). */
const FIELD_DOTS = [
  "bg-sky-500", "bg-emerald-500", "bg-amber-500", "bg-violet-500", "bg-rose-500",
  "bg-teal-500", "bg-indigo-500", "bg-orange-500", "bg-lime-500", "bg-fuchsia-500",
  "bg-cyan-500", "bg-red-500", "bg-green-500", "bg-purple-500",
];

function fieldHash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h;
}

export function fieldBadgeClass(field: string): string {
  if (field === "공통") return "bg-slate-800 text-white";
  return FIELD_BADGES[fieldHash(field) % FIELD_BADGES.length];
}

export function fieldDotClass(field: string): string {
  if (field === "공통") return "bg-slate-800";
  return FIELD_DOTS[fieldHash(field) % FIELD_DOTS.length];
}

/** 로컬(KST) 기준 YYYY-MM-DD 키. */
export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

/** 월간 캘린더 42셀(6주) — 해당 월 1일이 속한 주의 일요일부터. */
export function monthGridDays(monthDate: Date): Date[] {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // 일요일 시작
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** 42셀 범위를 [from, to) ISO 로 — 목록 API 요청 범위. */
export function gridRangeIso(monthDate: Date): { from: string; to: string } {
  const days = monthGridDays(monthDate);
  const from = days[0];
  const to = new Date(days[41]);
  to.setDate(to.getDate() + 1); // 마지막 셀 다음날 0시(미포함)
  return { from: from.toISOString(), to: to.toISOString() };
}

export function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const DOW = ["일", "월", "화", "수", "목", "금", "토"];

export function fmtDayHeading(key: string): string {
  const d = new Date(`${key}T00:00:00`);
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${DOW[d.getDay()]})`;
}

/** 짧은 날짜 라벨 — 오늘/내일은 말로, 그 밖은 'M/D(요일)'. 홈 '다가오는 일정' 용. */
export function fmtRelativeDay(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (isSameDay(d, today)) return "오늘";
  if (isSameDay(d, tomorrow)) return "내일";
  return `${d.getMonth() + 1}/${d.getDate()}(${DOW[d.getDay()]})`;
}

export function fmtDateTimeRange(s: ScheduleItem): string {
  const st = new Date(s.start_at);
  const en = new Date(s.end_at);
  const date = `${st.getFullYear()}-${String(st.getMonth() + 1).padStart(2, "0")}-${String(st.getDate()).padStart(2, "0")} (${DOW[st.getDay()]})`;
  const sameDay = isSameDay(st, en);
  const endPart = sameDay
    ? fmtTime(s.end_at)
    : `${en.getMonth() + 1}/${en.getDate()} ${fmtTime(s.end_at)}`;
  return `${date} ${fmtTime(s.start_at)} ~ ${endPart}`;
}

/** Date → <input type="datetime-local"> 값(로컬 기준). */
export function toLocalInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${dd}T${h}:${mi}`;
}

/** datetime-local 값(로컬) → ISO 8601(오프셋 포함 → UTC 변환은 Date 가 수행). */
export function localInputToIso(v: string): string {
  return new Date(v).toISOString();
}

/** 날짜별 그룹핑(로컬 기준 start_at) — 키 오름차순. */
export function groupByDay(items: ScheduleItem[]): [string, ScheduleItem[]][] {
  const map = new Map<string, ScheduleItem[]>();
  for (const it of items) {
    const k = dayKey(new Date(it.start_at));
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(it);
  }
  return [...map.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
}
